/**
 * Die Phasen- und Aufgabenliste des Reiters „Tasks" - Portierung von `taskPhaseHtml`
 * (`index.html:8984-9009`) und `taskRowHtml` (`:8971-8982`), samt der vier reinen Helfern, die beide
 * tragen: `taskPhaseKey` (`:8870-8872`), `taskBelongsToPhase` (`:8899-8903`), `taskPhaseDefinition`
 * (`:8893-8897`) und `taskPhaseDetail` (`:8882-8891`). Die Gruppierung selbst - welche Phasen
 * ueberhaupt erscheinen, welche Aufgaben in die Sammelphase "Aufgaben"/"Eigene Aufgaben" fallen -
 * ist die Portierung des Kopfteils von `drawerTasksTab` (`:9130-9140`), hier als `aufgabenPhasenGruppen`
 * exportiert, weil sowohl `PhasenListe` als auch `TasksTab.tsx` (Notizen-Sichtbarkeit) und
 * `AufgabenDetail.tsx` (Phasendefinition beim Oeffnen) sie brauchen.
 *
 * ================= Der Sammelphasen-Fall =================
 *
 * Ist die Bestellung keine "Vorlagenbestellung" (`istVorlagenBestellung` - `templateOrder` im
 * Original), erscheinen die sechs `DEFAULT_ORDER_TASK_PHASES` GAR NICHT; jede Aufgabe landet in
 * EINER Sammelphase mit der synthetischen Definition `{ id: 'custom', title: 'Aufgaben', ... }`
 * (`:9138-9139`). Ist sie eine Vorlagenbestellung, erscheinen ALLE sechs Phasen - auch leere -, und
 * nur Aufgaben mit einer UNBEKANNTEN `phaseId` fallen zusaetzlich in eine Sammelphase, die dann
 * "Eigene Aufgaben" heisst (`:9138`). Die Baseline `drawer-tasks` (`best-03`, kein
 * `taskTemplateVersion`) nimmt den ersten Fall: eine einzige Sammelphase "Aufgaben" mit den zwei
 * vorhandenen Aufgaben.
 *
 * ================= Zwei Sitzungs-Speicher, wie `SEC_COLLAPSED` (Task 16) ======================
 *
 * `TASK_PHASE_COLLAPSED` (`index.html:8868`) ist ein Modul-Global, geschluesselt ueber
 * `taskPhaseKey(orderId, phaseId)` - eingeklappte Phasen bleiben ueber `closeDrawer`/`openDrawer`
 * hinweg zu, auch fuer eine ANDERE Bestellung (der Schluessel enthaelt zwar die Bestell-id, aber das
 * Objekt selbst lebt pro Sitzung, nicht pro Drawer-Oeffnung). Wie bei `Abschnitt.tsx` haelt
 * `TaskPhaseBlock` den Anzeigestand zusaetzlich in `useState`, weil React (anders als `classList.
 * toggle` im Bestand) das Klassenattribut selbst besitzt; das Modul-Global wird beim Umschalten
 * mitgeschrieben.
 *
 * ================= Das Kontextmenue - NACHGEHOLT (Auftrag "Tasks Sammelbearbeitung", 2026-09-04) ==
 *
 * `taskRowHtml` trug `oncontextmenu="openTaskContextMenu(event,${index})"` (`:8975`), auf ein
 * eigenes `#taskContextMenu` (`index.html:3818`, statische Flaeche der App-Huelle) - beim
 * urspruenglichen React-Umbau bewusst NICHT nachgezogen (siehe Historie dieses Kommentars). Die
 * Anforderung von Damian Gembries (Asana 1218053584680826, 04.09.2026: "bekommen wir das auch fuer
 * die Untertasks hin?") verlangt genau das Gegenstueck zum bereits gebauten Board-Feature
 * (`Board/BoardSeite.tsx#zeileAngeklickt`, `Board/KontextMenue.tsx`) - hier nachgeholt, EIGENES
 * Menue (`AufgabenKontextMenue.tsx`, kein `#taskContextMenu`-Wiederaufbau) statt der Bestandsflaeche,
 * dieselbe Entscheidung wie beim Board (auch dort kein Nachbau von `#orderContextMenu` als
 * statischer App-Huellen-Knoten).
 *
 * `AufgabenZeile` reicht dafuer zwei neue, optionale Ereignisse NUR DURCH (keine eigene
 * Auswahl-/Oeffnen-Entscheidung hier, exakt wie `Bestellzeile.tsx` fuers Board):
 * `onZeileAngeklickt` (Maus-`onClick`, ersetzt das bisherige `onClick={oeffnen}` - `TasksTab.tsx#
 * zeileAngeklickt` liest Strg/Shift/einfachen Klick selbst) und `onKontextmenue` (`onContextMenu`,
 * NEU). Tastatur (`onKeyDown`, Enter/Leertaste) bleibt UNVERAENDERT an `onOeffnen` haengen - anders
 * als ein Mausklick traegt sie hier keine sinnvolle Strg/Shift-Mehrfachauswahl-Semantik, ein
 * Tastaturnutzer soll die Aufgabe weiterhin einfach oeffnen koennen.
 */
import { useState } from "react";
import { DEFAULT_ORDER_TASK_PHASES } from "../../../config/orderTaskPhases.ts";
import type { OrderTaskPhase } from "../../../config/orderTaskPhases.ts";
import { fmtDate, NUM } from "../../../lib/format.ts";
import type { Order, OrderTask } from "../../../lib/types.ts";

/**
 * `icon(name, className)` (`index.html:4016`) samt der Nachbearbeitung durch `hydrateIcons`
 * (`:4021`) - die gemeinsame Registratur zeichnet das fertige `<svg class="lucide lucide-…">`.
 *
 * Bis `feat/09-spa-ikonen` stand hier eine LOKALE Funktion, die nur den Platzhalter
 * `<i data-lucide="…">` erzeugte. Den hydriert in der SPA niemand (die UMD-Fassung laeuft allein
 * im Baseline-Rundlauf des Playwright-Harness) - es blieb eine leere Flaeche. Siehe `Ikone.tsx`.
 */
import { Ikone } from "../../Ikone.tsx";

/** `index.html:8868`, woertlich - Modul-Global, damit eingeklappte Phasen die Sitzung ueberleben. */
export const TASK_PHASE_COLLAPSED: Record<string, boolean> = {};

/** Nur fuer Tests: den Sitzungsstand auf den Anfangswert des Bestands (leer) zuruecksetzen. */
export function taskPhaseCollapsedZuruecksetzen(): void {
  for (const schluessel of Object.keys(TASK_PHASE_COLLAPSED)) delete TASK_PHASE_COLLAPSED[schluessel];
}

/** `taskPhaseKey` (`index.html:8870-8872`), woertlich. */
export function taskPhaseKey(orderId: string, phaseId: string): string {
  return `${orderId}__${phaseId}`;
}

/** `taskBelongsToPhase` (`index.html:8899-8903`), woertlich. */
export function taskBelongsToPhase(task: OrderTask, phaseId: string): boolean {
  if (phaseId !== "custom") return ((task.phaseId as string) || "custom") === phaseId;
  const bekannt = new Set(DEFAULT_ORDER_TASK_PHASES.map((phase) => phase.id));
  return !bekannt.has(task.phaseId as string);
}

/** `taskPhaseDefinition` (`index.html:8893-8897`), woertlich. */
export function taskPhaseDefinition(
  o: Order,
  phaseId: string,
): Pick<OrderTaskPhase, "id" | "title" | "description"> {
  const bekannt = DEFAULT_ORDER_TASK_PHASES.find((phase) => phase.id === phaseId);
  if (bekannt) return bekannt;
  return { id: phaseId, title: o.taskTemplateVersion ? "Eigene Aufgaben" : "Aufgaben", description: "" };
}

export interface AufgabenPhaseDetail {
  name: string;
  description: string;
  done: boolean;
  assignee: string;
  due: string;
}

/** `taskPhaseDetail` (`index.html:8882-8891`), woertlich. */
export function taskPhaseDetail(
  o: Order,
  phase: Pick<OrderTaskPhase, "id" | "title" | "description">,
): AufgabenPhaseDetail {
  const gespeichert = ((o.taskPhaseDetails as Record<string, Record<string, unknown>> | undefined) ||
    {})[phase.id] || {};
  return {
    name: Object.prototype.hasOwnProperty.call(gespeichert, "name")
      ? (gespeichert.name as string)
      : phase.title,
    description: Object.prototype.hasOwnProperty.call(gespeichert, "description")
      ? (gespeichert.description as string)
      : phase.description || "",
    done: !!gespeichert.done,
    assignee: (gespeichert.assignee as string) || "",
    due: (gespeichert.due as string) || "",
  };
}

/** `templateOrder` (`index.html:9133`), woertlich. */
export function istVorlagenBestellung(o: Order): boolean {
  const tasks = (o.tasks as OrderTask[] | undefined) || [];
  const bekannt = new Set(DEFAULT_ORDER_TASK_PHASES.map((phase) => phase.id));
  return !!o.taskTemplateVersion || tasks.some((task) => bekannt.has(task.phaseId as string));
}

export interface AufgabenPhaseGruppe {
  phase: Pick<OrderTaskPhase, "id" | "title" | "description">;
  eintraege: { aufgabe: OrderTask; index: number }[];
}

/**
 * Der Kopfteil von `drawerTasksTab` (`index.html:9130-9140`): welche Phasen erscheinen und welche
 * Aufgaben in jede fallen. Siehe Kopfkommentar, "Der Sammelphasen-Fall".
 */
export function aufgabenPhasenGruppen(o: Order): AufgabenPhaseGruppe[] {
  const tasks = (o.tasks as OrderTask[] | undefined) || [];
  const bekannt = new Set(DEFAULT_ORDER_TASK_PHASES.map((phase) => phase.id));
  const vorlage = istVorlagenBestellung(o);
  const eingebettet = tasks.map((aufgabe, index) => ({ aufgabe, index }));

  const gruppen: AufgabenPhaseGruppe[] = vorlage
    ? DEFAULT_ORDER_TASK_PHASES.map((phase) => ({
        phase,
        eintraege: eingebettet.filter((eintrag) => eintrag.aufgabe.phaseId === phase.id),
      }))
    : [];

  const eigene = eingebettet.filter((eintrag) => !bekannt.has(eintrag.aufgabe.phaseId as string));
  if (eigene.length) {
    gruppen.push({
      phase: { id: "custom", title: vorlage ? "Eigene Aufgaben" : "Aufgaben", description: "" },
      eintraege: eigene,
    });
  }
  return gruppen;
}

interface AufgabenZeileProps {
  aufgabe: OrderTask;
  index: number;
  heute: string;
  onOeffnen: (index: number) => void;
  onErledigt: (index: number, erledigt: boolean) => void;
  /**
   * Auftrag "Tasks Sammelbearbeitung": `true`, wenn diese Zeile in `TasksTab`s Mehrfachauswahl
   * steht - setzt zusaetzlich zu `done`/`overdue` die Klasse `task-row-selected`
   * (`styles/neubau/mehrfachauswahl.css`), alle drei koennen gleichzeitig stehen.
   */
  ausgewaehlt?: boolean;
  /**
   * Der VOLLE Maus-Klick-Handler der Zeile (ersetzt das bisherige `onClick={oeffnen}`) - `TasksTab.
   * tsx#zeileAngeklickt` liest Strg/Cmd/Shift des durchgereichten `MouseEvent` selbst und entscheidet
   * dort, ob der Klick die Auswahl aendert oder die Aufgabe oeffnet. Siehe Kopfkommentar dieser
   * Datei, Abschnitt "Das Kontextmenue".
   */
  onZeileAngeklickt?: (ereignis: React.MouseEvent, index: number) => void;
  /** `oncontextmenu="openTaskContextMenu(event,${index})"` (`index.html:8975`), nachgeholt. */
  onKontextmenue?: (ereignis: React.MouseEvent, index: number) => void;
}

/** `taskRowHtml` (`index.html:8971-8982`). */
function AufgabenZeile({
  aufgabe,
  index,
  heute,
  onOeffnen,
  onErledigt,
  ausgewaehlt,
  onZeileAngeklickt,
  onKontextmenue,
}: AufgabenZeileProps) {
  const ueberfaellig = !aufgabe.done && !!aufgabe.due && (aufgabe.due as string) < heute;
  const meta = [aufgabe.assignee as string, aufgabe.due ? fmtDate(aufgabe.due as string) : ""]
    .filter(Boolean)
    .join(" · ");
  const oeffnen = (): void => onOeffnen(index);

  return (
    <div
      className={[
        "task-row",
        aufgabe.done ? "done" : "",
        ueberfaellig ? "overdue" : "",
        ausgewaehlt ? "task-row-selected" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      role="button"
      tabIndex={0}
      title="Öffnen · Rechtsklick für Aktionen"
      onClick={(e) => onZeileAngeklickt?.(e, index)}
      onContextMenu={(e) => onKontextmenue?.(e, index)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          oeffnen();
        }
      }}
    >
      <input
        className="task-row-check"
        type="checkbox"
        checked={!!aufgabe.done}
        aria-label="Task erledigt"
        data-permission={aufgabe.kind === "payment_request" ? "request_payment" : undefined}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => {
          e.stopPropagation();
          onErledigt(index, e.currentTarget.checked);
        }}
      />
      <span className="task-row-name">{(aufgabe.name as string) || "Neue Aufgabe"}</span>
      <span className="task-row-meta">
        {aufgabe.responsibilityName ? (
          <span className="responsibility-badge">
            <Ikone name="blocks" />
            {aufgabe.responsibilityName as string}
          </span>
        ) : null}
        {meta ? <span>{meta}</span> : null}
      </span>
      <span className="task-row-chevron">
        <Ikone name="arrow-right" />
      </span>
    </div>
  );
}

interface TaskPhaseBlockProps {
  bestellung: Order;
  gruppe: AufgabenPhaseGruppe;
  heute: string;
  onAufgabeOeffnen: (index: number) => void;
  onPhaseOeffnen: (phaseId: string) => void;
  onAufgabeErledigt: (index: number, erledigt: boolean) => void;
  onPhaseErledigt: (taskIds: string[], erledigt: boolean) => void;
  /** Auftrag "Tasks Sammelbearbeitung" - die Ids der aktuell markierten Aufgaben, siehe `AufgabenZeile
   *  Props.ausgewaehlt`. `TaskPhaseBlock` prueft je Zeile `.has(...)`, wie `BoardSeite.tsx` es fuer
   *  `Bestellzeile` einen Aufruf hoeher tut. */
  ausgewaehlteIds: ReadonlySet<string>;
  onZeileAngeklickt?: (ereignis: React.MouseEvent, index: number) => void;
  onKontextmenue?: (ereignis: React.MouseEvent, index: number) => void;
}

/** `taskPhaseHtml` (`index.html:8984-9009`) samt `toggleTaskPhase` (`:8874-8880`). */
function TaskPhaseBlock({
  bestellung: o,
  gruppe,
  heute,
  onAufgabeOeffnen,
  onPhaseOeffnen,
  onAufgabeErledigt,
  onPhaseErledigt,
  ausgewaehlteIds,
  onZeileAngeklickt,
  onKontextmenue,
}: TaskPhaseBlockProps) {
  const { phase, eintraege } = gruppe;
  const key = taskPhaseKey(o.id as string, phase.id);
  const [zu, setZu] = useState<boolean>(() => !!TASK_PHASE_COLLAPSED[key]);
  const detail = taskPhaseDetail(o, phase);
  const erledigtAnzahl = eintraege.filter((eintrag) => eintrag.aufgabe.done).length;
  const phaseDone = eintraege.length ? erledigtAnzahl === eintraege.length : detail.done;

  const umschalten = (): void => {
    // Reihenfolge wie im Original: erst das Sitzungs-Global, dann die Darstellung.
    TASK_PHASE_COLLAPSED[key] = !TASK_PHASE_COLLAPSED[key];
    setZu(!!TASK_PHASE_COLLAPSED[key]);
  };

  /**
   * Auftrag "Meilensteine (Phasen) im Tasks-Tab abhakbar machen": sammelt die Task-IDs der Gruppe
   * und meldet sie samt dem gewuenschten Zielzustand nach oben.
   *
   * Aufgaben ohne `id` (siehe `AufgabenDetail`/`TasksTab.tsx#setAnsicht`, "Risiko 7": eine frisch
   * angelegte Aufgabe hat kurzzeitig noch keine) werden herausgefiltert statt eine leere
   * Zeichenkette an den Bulk-Aufruf zu reichen - eine leere id traefe serverseitig ohnehin keine
   * Zeile (`ANY($..::uuid[])`), waere hier aber ein sinnloser Eintrag in der Liste.
   */
  const taskIdsDerGruppe = (): string[] =>
    eintraege.map((eintrag) => String(eintrag.aufgabe.id ?? "")).filter((id) => id.length > 0);

  return (
    <div
      className={`task-phase ${zu ? "collapsed" : ""} ${phaseDone ? "done" : ""}`}
      data-task-phase-key={key}
    >
      <div className="task-phase-head">
        <button
          className="task-phase-toggle"
          type="button"
          aria-expanded={zu ? "false" : "true"}
          aria-label={zu ? "Phase ausklappen" : "Phase einklappen"}
          onClick={umschalten}
        >
          <span className="task-phase-arrow collapse-pair">
            <Ikone name="chevrons-right" className="collapse-closed" />
            <Ikone name="chevrons-down" className="collapse-open" />
          </span>
        </button>
        {/*
          Auftrag "Meilensteine (Phasen) im Tasks-Tab abhakbar machen": KEIN eigenes `disabled`
          mehr. Wie `.task-row-check` in `AufgabenZeile` traegt dieses Bedienelement kein eigenes
          `data-permission` - es folgt damit derselben Quelle wie die einzelnen Task-Checkboxen,
          dem Rechte-Durchgang des Rahmens (`Drawer.tsx`, `canEditTab` = `manage_tasks` fuer den
          Tasks-Reiter, `DRAWER_TAB_EDIT_PERMISSIONS.tasks`). Ohne `manage_tasks` sperrt der
          Durchgang es NACH dem Zeichnen automatisch - kein eigener Sonderfall fuer die Phase mehr.
          `disabled` (nicht `readOnly`) bleibt trotzdem die richtige Wahl FUER den Durchgang selbst:
          bei einer Checkbox verhindert `readOnly` das Umschalten nicht (nur bei Text-/Passwortfeldern
          wirksam) - der Durchgang setzt deshalb `control.disabled = true`, nicht `readOnly`.
        */}
        <input
          className="task-phase-check"
          type="checkbox"
          checked={phaseDone}
          aria-label={`${detail.name} vollständig erledigen`}
          title={phaseDone ? "Phase wieder öffnen" : "Alle Tasks dieser Phase erledigen"}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => {
            e.stopPropagation();
            const taskIds = taskIdsDerGruppe();
            if (taskIds.length === 0) return;
            onPhaseErledigt(taskIds, e.currentTarget.checked);
          }}
        />
        <button className="task-phase-open" type="button" onClick={() => onPhaseOeffnen(phase.id)}>
          <span className="task-phase-title">{detail.name}</span>
          <span className="task-phase-progress">
            {phaseDone ? "Erledigt" : `${NUM.format(erledigtAnzahl)}/${NUM.format(eintraege.length)}`}{" "}
            <Ikone name="arrow-right" />
          </span>
        </button>
      </div>
      <div className="task-phase-body">
        <div className="task-list">
          {eintraege.length ? (
            eintraege.map((eintrag) => (
              <AufgabenZeile
                key={eintrag.aufgabe.id as string}
                aufgabe={eintrag.aufgabe}
                index={eintrag.index}
                heute={heute}
                onOeffnen={onAufgabeOeffnen}
                onErledigt={onAufgabeErledigt}
                ausgewaehlt={ausgewaehlteIds.has(String(eintrag.aufgabe.id ?? ""))}
                onZeileAngeklickt={onZeileAngeklickt}
                onKontextmenue={onKontextmenue}
              />
            ))
          ) : (
            <div className="task-phase-empty">Keine Aufgaben in dieser Phase.</div>
          )}
        </div>
      </div>
    </div>
  );
}

export interface PhasenListeProps {
  bestellung: Order;
  heute: string;
  onAufgabeOeffnen: (index: number) => void;
  onPhaseOeffnen: (phaseId: string) => void;
  onAufgabeErledigt: (index: number, erledigt: boolean) => void;
  /**
   * Auftrag "Meilensteine (Phasen) im Tasks-Tab abhakbar machen": die Phasen-Checkbox meldet die
   * Task-IDs ALLER Eintraege ihrer Gruppe (bekannte Phase ODER die Sammelgruppe "Eigene
   * Aufgaben"/"Aufgaben") samt dem gewuenschten Zielzustand - `PATCH .../tasks/bulk-done`
   * (`api/aufgaben.ts#aufgabenBulkErledigen`, gerufen aus `TasksTab.tsx`).
   */
  onPhaseErledigt: (taskIds: string[], erledigt: boolean) => void;
  /** Auftrag "Tasks Sammelbearbeitung" - siehe `TaskPhaseBlockProps.ausgewaehlteIds`. */
  ausgewaehlteIds: ReadonlySet<string>;
  onZeileAngeklickt?: (ereignis: React.MouseEvent, index: number) => void;
  onKontextmenue?: (ereignis: React.MouseEvent, index: number) => void;
}

/** Der `phaseHtml + customPhase`-Teil von `drawerTasksTab` (`index.html:9134-9140,9147`). */
export function PhasenListe({
  bestellung: o,
  heute,
  onAufgabeOeffnen,
  onPhaseOeffnen,
  onAufgabeErledigt,
  onPhaseErledigt,
  ausgewaehlteIds,
  onZeileAngeklickt,
  onKontextmenue,
}: PhasenListeProps) {
  const gruppen = aufgabenPhasenGruppen(o);
  if (!gruppen.length) return <div className="empty-note">Noch keine Tasks für diese Bestellung.</div>;
  return (
    <>
      {gruppen.map((gruppe) => (
        <TaskPhaseBlock
          key={gruppe.phase.id}
          bestellung={o}
          gruppe={gruppe}
          heute={heute}
          onAufgabeOeffnen={onAufgabeOeffnen}
          onPhaseOeffnen={onPhaseOeffnen}
          onAufgabeErledigt={onAufgabeErledigt}
          onPhaseErledigt={onPhaseErledigt}
          ausgewaehlteIds={ausgewaehlteIds}
          onZeileAngeklickt={onZeileAngeklickt}
          onKontextmenue={onKontextmenue}
        />
      ))}
    </>
  );
}
